import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { FC, Fragment, useMemo, useState } from 'react'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { GRAY_800, YELLOW_100 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'
import { MUIButton } from 'components/common/MUIButton'
import { useGalleryAssignment, useGalleryDeal } from '../../_main/contexts'

import { AssignmentChangelogPopup } from './AssignmentChangelog/AssignmentChangelogPopup.module'
import { AssignmentDTOIsAdministratorDTO } from 'utils/typeguards'
import { AssignmentDeliverySpeed } from 'models/assignment'
import { AssignmentLabels } from 'constants/assignment'
import { Badge } from 'components/common/Badges'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import SyncIcon from '@mui/icons-material/Sync'
import { SystemMessage } from 'components/common/SystemMessage'
import { choosePrimaryProduct } from 'utils/product'
import { getDateString } from 'utils/time/timeUtils'
import styles from './AssignmentDetailsCard.module.sass'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useSyncAssignment } from 'dataQueries'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props Input properties of AssignmentDetailsCard component
 */
interface Props {
  /** ID of the selected assignment */
  assignmentId: string
  /** Optional callback to open the assignment detail drawer */
  onSeeDetails?: () => void
}

/**
 * Display assignment details card in the gallery sidebar for administrators.
 * 
 * @example <AssignmentDetailsCard assignmentId={102385} />
 */
export const AssignmentDetailsCard: FC<Props> = ({ assignmentId, onSeeDetails }) => {
  const { spawnSuccessToast } = useSnackbar()
  const { t } = useTranslation(['assignment_stage', 'deal_assignment', 'gallery', 'product_kind', 'schedule_meeting_popup', 'enums'])

  const { assignmentData, assignmentStage, isAssignmentCancelled } = useGalleryAssignment()
  const { dealId } = useGalleryDeal()
  const showCancelledLines = useFeatureFlag(FeatureFlag.ENABLE_SF_LAST_LINE_CANCEL)

  const syncAssignment = useSyncAssignment()

  const [changelogPopup, setChangelogPopup] = useState(false)
  const assignmentAutomationStatus = useMemo(() => (assignmentData && AssignmentDTOIsAdministratorDTO(assignmentData)) ? assignmentData.assignmentAutomationStatus : undefined, [assignmentData])
  const product = useMemo(() => choosePrimaryProduct(assignmentData?.products), [assignmentData])

  if (!assignmentData || !AssignmentDTOIsAdministratorDTO(assignmentData)) return <Fragment></Fragment>

  return (
    <div className="details noPadding">

      {/** Assignment Badges (stage and status) */}
      <div className={styles.wrapperBadges}>
        <Badge color="gray">{t(`assignment_stage:admin:${assignmentStage}`)}</Badge>
        {showCancelledLines && isAssignmentCancelled &&
          <Badge color="red">{t('deal_assignment:cancelled')}</Badge>
        }
        {!!assignmentData &&
          <Fragment>
            {assignmentData.assignmentLabels.includes(AssignmentLabels.IMPORTANT) &&
              <Badge color="red">{t('deal_assignment:important_assignment')}</Badge>
            }
            {assignmentData.deliverySpeed === AssignmentDeliverySpeed.FAST &&
              <Badge color="blue">{t('deal_assignment:fast_delivery')}</Badge>
            }
            {assignmentData.scheduledByCT &&
              <Badge color="orange">{t('deal_assignment:scheduled_by_ct')}</Badge>
            }
          </Fragment>
        }
      </div>

      {/** Assignment description (product kind and shooting date) */}
      <div className={styles.description}>
        <h2>
          {product
            ? (
              product.quantity > 1 ? `${product.quantity} x ` : ''
            ) + t(`product_kind:${product.kind}`) + ' - ' + t(`product:p_${product.id}`)
            : 'N/A'
          }
        </h2>
        {!!assignmentData?.shootingStartDateTime && assignmentData?.shootingDuration > 0 &&
          <span className={styles.textDate}>
            {getDateString(assignmentData.shootingStartDateTime)}
          </span>
        }
      </div>

      {/* Late CT schedule */}
      {assignmentData.scheduledByCT && !!assignmentData.lateCtSchedulingReason &&
        <BorderBoxWrapper
          className={styles.lateSchedule}
          backgroundColor={YELLOW_100}
          elevation="none"
          borderColor={YELLOW_100}
          p={1}
          m={2}
        >
          <Stack gap={1}>

            <h4>{t('deal_assignment:scheduled_late')}</h4>

            <p>{t('deal_assignment:reason')}: <span className={styles.darker}>{t(`deal_assignment_card:schedule_meeting_popup.reasons.${assignmentData.lateCtSchedulingReason}`)}</span></p>

            {!!assignmentData.lateCtSchedulingComment &&
              <BorderBoxWrapper elevation="none" p={1}>
                <p>{assignmentData.lateCtSchedulingComment}</p>
              </BorderBoxWrapper>
            }

          </Stack>
        </BorderBoxWrapper>
      }

      {!!assignmentData.lessThanRequiredVisualsReason &&
        <SystemMessage
          sx={{ m: 2 }}
          variant="warning"
          title={t('gallery:less_than_required_visuals_title')}
          message={(
            <Stack gap={.2}>
              <Typography variant="text-sm" fontWeight="bold">
                {t(`enums:less_than_required_visuals_reason.${assignmentData.lessThanRequiredVisualsReason}`)}
              </Typography>
              {!!assignmentData.lessThanRequiredVisualsComment &&
                <Typography variant="text-sm" fontStyle="italic" color={GRAY_800}>
                  "{assignmentData.lessThanRequiredVisualsComment}"
                </Typography>
              }
            </Stack>
          )}
        />
      }

      <Stack className={styles.wrapperFooter} gap={1} direction="row">

        {/** See details */}
        {!!onSeeDetails &&
          <MUIButton
            type="secondaryBorder"
            startIcon={<InfoOutlinedIcon fontSize="large" />}
            onClick={onSeeDetails}
          >
            {t('gallery:order_informations.see_details')}
          </MUIButton>
        }

        {/** Changelog */}
        {!!assignmentAutomationStatus && !!assignmentStage &&
          <Fragment>
            <MUIButton
              type="secondaryBorder"
              startIcon={<FormatListBulletedIcon fontSize="large" />}
              onClick={() => setChangelogPopup(true)}
            >
              {t('gallery:showcase_changelog')}
            </MUIButton>

            <AssignmentChangelogPopup
              isOpen={changelogPopup}
              assignmentId={assignmentId}
              onClickOutside={() => setChangelogPopup(false)}
              onClickClose={() => setChangelogPopup(false)}
            />
          </Fragment>
        }

        {/** Sync assignment */}
        <MUIButton
          type="secondaryBorder"
          startIcon={<SyncIcon fontSize="large" />}
          disabled={syncAssignment.isPending}
          isLoading={syncAssignment.isPending}
          onClick={() => syncAssignment.mutate({ assignmentId, orderId: dealId }, {
            onSuccess: () => {
              spawnSuccessToast(t('deal_assignment:assignment_synchronized'))
            }
          })}
        >
          {t('deal_assignment:sync_assignment')}
        </MUIButton>

      </Stack>

    </div>
  )
}
